import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Calculator, Scale, Loader2, Info, Mail, CheckCircle2, ChevronDown, Store, Stethoscope, Users, Building2, Minus, Plus, Pencil, FileText, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { AnzahlungFeld, BetragInput, EinzugstagChips, KonditionenTabelle, LaufzeitChips, minAnzahlung, useRatenVorschau } from '../components/RatenShared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur0 = (v) => (typeof v === 'number' ? v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '—');
const eur = (v) => (typeof v === 'number' ? v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '—');
const num = (v) => (v === '' || v == null ? null : Number(String(v).replace(/\./g, '').replace(',', '.')));
const fmtIn = (n) => (n == null || Number.isNaN(n) ? '' : n.toLocaleString('de-DE'));
const field = 'w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm';
const label = 'block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2';

const useDebouncedCalc = (url, body, enabled = true) => {
  const [erg, setErg] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = JSON.stringify(body);
  const t = useRef();
  useEffect(() => {
    if (!enabled) { setErg(null); return undefined; }
    setBusy(true);
    clearTimeout(t.current);
    t.current = setTimeout(async () => {
      try { setErg((await axios.post(`${API}${url}`, JSON.parse(key))).data); }
      catch (e) { console.warn('[honorar]', e?.response?.data || e.message); }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(t.current);
  }, [url, key, enabled]);
  return { erg, busy };
};

const Chips = ({ values, current, onPick, testid }) => (
  <div className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
    {values.map((v) => <button key={v} type="button" onClick={() => onPick(v)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${current === v ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'border-gray-200 text-[#34536b] hover:border-[#b8893d]'}`}>{eur0(v)}</button>)}
  </div>
);

const MoneyInput = ({ value, onChange, testid, placeholder }) => (
  <div className="relative">
    <input className={`${field} pr-9 text-lg`} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7a8a99] text-sm">€</span>
  </div>
);

const Stepper = ({ value, onChange, testid }) => {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center border border-gray-200 rounded-sm bg-white" data-testid={testid}>
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))} className="px-3 py-3 text-[#1B3A5C] hover:bg-gray-50" aria-label={t('honorar.stepper.weniger')}><Minus className="w-4 h-4" /></button>
      <input className="w-16 text-center text-lg py-2 outline-none" type="number" min="0" value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => onChange(value + 1)} className="px-3 py-3 text-[#1B3A5C] hover:bg-gray-50" aria-label={t('honorar.stepper.mehr')}><Plus className="w-4 h-4" /></button>
    </div>
  );
};

const Hinweise = ({ items, testid }) => (
  <ul className="space-y-1.5 text-xs text-[#7a8a99] leading-relaxed" data-testid={testid}>
    {items.map((h) => <li key={h} className="flex gap-2"><Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#b8893d]" />{h}</li>)}
  </ul>
);

const Details = ({ children, testid }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-5">
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs tracking-[0.15em] uppercase text-[#b8893d] hover:text-[#8f6a2b]" data-testid={testid}>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} /> {open ? t('honorar.details.ausblenden') : t('honorar.details.zusammensetzung')}
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
};

const bruttoAusErgebnis = (typ, erg) => {
  if (!erg) return 0;
  if (typ === 'rvg') return erg.brutto || 0;
  if (typ === 'stbvv') return erg.jahr?.mittel?.brutto || 0;
  return erg.gesamt?.mittel?.brutto || 0;
};

const RatenOption = ({ typ, ergebnis, raten, setRaten }) => {
  const { t } = useTranslation();
  const { vorschau } = useRatenVorschau(raten.aktiv ? raten.gesamtbetrag : 0, raten.anzahlung, raten.laufzeit_monate, raten.einzugstag || 1);
  return (
    <div className="mt-4 border border-gray-100 rounded-sm p-4 bg-[#fbf7f0]/60" data-testid="anfrage-raten">
      <label className="flex items-start gap-3 text-sm text-[#1B3A5C] cursor-pointer">
        <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d]" checked={raten.aktiv} onChange={(e) => { const g = raten.gesamtbetrag || Math.round(bruttoAusErgebnis(typ, ergebnis)); setRaten({ ...raten, aktiv: e.target.checked, gesamtbetrag: g, anzahlung: Math.max(Number(raten.anzahlung) || 0, minAnzahlung(g, raten.laufzeit_monate)) }); }} data-testid="anfrage-raten-toggle" />
        <span><span className="font-medium">{t('raten.hr.toggle')}</span><span className="block text-xs text-[#7a8a99] mt-0.5">{t('raten.hr.toggle_hint')}</span></span>
      </label>
      {raten.aktiv && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[10px] tracking-wider text-[#7a8a99] uppercase mb-1">{t('raten.form.gesamtbetrag')}</label><BetragInput className={field} value={raten.gesamtbetrag} onChange={(v) => setRaten({ ...raten, gesamtbetrag: v })} data-testid="anfrage-raten-betrag" /></div>
            <AnzahlungFeld gesamtbetrag={raten.gesamtbetrag} anzahlung={raten.anzahlung} laufzeit={raten.laufzeit_monate} onChange={(v) => setRaten((r) => ({ ...r, anzahlung: v }))} field={field} labelCls="block text-[10px] tracking-wider text-[#7a8a99] uppercase mb-1" testid="anfrage-raten-anzahlung" />
          </div>
          <LaufzeitChips value={raten.laufzeit_monate} onChange={(m) => setRaten({ ...raten, laufzeit_monate: m })} testid="anfrage-raten-laufzeit" />
          <EinzugstagChips value={raten.einzugstag || 1} onChange={(d) => setRaten({ ...raten, einzugstag: d })} testid="anfrage-raten-einzugstag" />
          {vorschau && <div className="bg-white rounded-sm p-3"><KonditionenTabelle b={vorschau} compact testid="anfrage-raten-konditionen" /></div>}
          <p className="text-[11px] text-[#7a8a99]">{t('raten.hr.hinweis')}</p>
        </div>
      )}
    </div>
  );
};

const AnfrageFormInner = ({ typ, eingaben, ergebnis }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || '', email: user?.email || '', phone: '', nachricht: '', datenschutz: false });
  const [raten, setRaten] = useState({ aktiv: false, gesamtbetrag: '', anzahlung: '', laufzeit_monate: 6, einzugstag: 1 });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!f.datenschutz) { toast({ title: t('honorar.anfrage.fehler_datenschutz'), variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const ratenwunsch = raten.aktiv ? { gesamtbetrag: Number(raten.gesamtbetrag), anzahlung: Number(raten.anzahlung) || 0, laufzeit_monate: raten.laufzeit_monate } : null;
      await axios.post(`${API}/honorar/anfrage`, { typ, eingaben, ergebnis, ...f, ratenwunsch });
      if (ratenwunsch) {
        await axios.post(`${API}/raten/anfrage`, { ...ratenwunsch, name: f.name, email: f.email, telefon: f.phone, nachricht: `Honorarrechner (${typ}): ${f.nachricht || '—'}`, quelle: 'honorarrechner', datenschutz: true });
      }
      setDone(true);
    }
    catch (err) { toast({ title: t('honorar.anfrage.fehler_titel'), description: err?.response?.data?.detail || t('honorar.anfrage.fehler_beschreibung'), variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  if (done) return <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-600 text-sm text-green-800 flex gap-2" data-testid="anfrage-success"><CheckCircle2 className="w-4 h-4 mt-0.5" />{t('honorar.anfrage.erfolg')}</div>;
  return (
    <form onSubmit={send} className="mt-6 bg-white border border-gray-100 rounded-sm p-5" data-testid="anfrage-form">
      <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-1">{t('honorar.anfrage.ueberschrift')}</div>
      <p className="text-sm text-[#34536b] mb-4">{t('honorar.anfrage.beschreibung')}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input className={field} placeholder={t('honorar.anfrage.name_placeholder')} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="anfrage-name" />
        <input className={field} type="email" placeholder={t('honorar.anfrage.email_placeholder')} value={f.email} readOnly title="E-Mail-Adresse Ihres Kontos" required data-testid="anfrage-email" />
        <input className={field} placeholder={t('honorar.anfrage.telefon_placeholder')} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="anfrage-phone" />
      </div>
      <textarea className={`${field} mt-3`} rows={2} placeholder={t('honorar.anfrage.nachricht_placeholder')} value={f.nachricht} onChange={(e) => setF({ ...f, nachricht: e.target.value })} data-testid="anfrage-nachricht" />
      <RatenOption typ={typ} ergebnis={ergebnis} raten={raten} setRaten={setRaten} />
      <label className="mt-3 flex items-start gap-3 text-xs text-[#34536b] cursor-pointer">
        <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d]" checked={f.datenschutz} onChange={(e) => setF({ ...f, datenschutz: e.target.checked })} data-testid="anfrage-datenschutz" />
        <span>{t('honorar.anfrage.datenschutz_text_vor')} <Link to="/datenschutz" target="_blank" className="text-[#b8893d] underline">{t('honorar.anfrage.datenschutz_link')}</Link> {t('honorar.anfrage.datenschutz_text_nach')}</span>
      </label>
      <button type="submit" disabled={busy} data-testid="anfrage-submit" className="mt-4 inline-flex items-center gap-2 bg-[#1B3A5C] hover:bg-[#15304d] disabled:opacity-60 text-white text-xs tracking-[0.2em] px-6 py-3 rounded-sm">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {t('honorar.anfrage.absenden')}
      </button>
    </form>
  );
};

const ResultCard = ({ kicker, big, bigLabel, range, sub, busy, testid, toggle, children }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-[#1B3A5C] text-white rounded-sm p-6 lg:p-8 relative overflow-hidden scroll-mt-[120px] lg:scroll-mt-40" data-testid={testid} id={testid}>
      <div className="absolute top-0 right-0 w-40 h-40 bg-[#b8893d] rounded-full blur-3xl opacity-20 pointer-events-none"></div>
      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-[11px] tracking-[0.28em] text-[#e0b56a] uppercase">{kicker}</div>
          {toggle && (
            <div className="inline-flex text-[10px] tracking-wider rounded-full border border-white/20 overflow-hidden flex-shrink-0" data-testid={`${testid}-toggle`}>
              {[[false, t('honorar.result.brutto')], [true, t('honorar.result.netto')]].map(([v, l]) => (
                <button key={String(v)} type="button" onClick={() => toggle.onChange(v)} data-testid={`${testid}-toggle-${v ? 'netto' : 'brutto'}`} className={`px-3 py-1 transition-colors ${toggle.checked === v ? 'bg-[#b8893d] text-white' : 'text-white/60 hover:text-white'}`}>{l}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="font-serif text-5xl md:text-6xl leading-none" data-testid={`${testid}-big`}>{busy ? <Loader2 className="w-10 h-10 animate-spin text-[#e0b56a]" /> : big}</div>
          <div className="text-white/70 text-sm pb-1">{bigLabel}</div>
        </div>
        {range && <div className="mt-3 text-white/80 text-sm" data-testid={`${testid}-range`}>{range}</div>}
        {sub && <div className="mt-1 text-white/60 text-xs">{sub}</div>}
        {toggle && <div className="mt-2 text-[10px] text-white/45">{t('honorar.result.netto_hinweis')}</div>}
        <div className="mt-3 text-[10px] text-white/50 tracking-wide" data-testid={`${testid}-gewaehr`}>{t('honorar.result.ohne_gewaehr')}</div>
        {children}
      </div>
    </div>
  );
};

const AnfrageForm = (props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="anfrage-open" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#b8893d] hover:bg-[#a07730] text-white text-xs tracking-[0.2em] px-6 py-4 rounded-sm transition-colors">
        <Mail className="w-4 h-4" /> {t('honorar.anfrage.button')}
      </button>
    );
  }
  return <AnfrageLoginGate {...props} />;
};

const AnfrageLoginGate = (props) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  if (isAuthenticated) return <AnfrageFormInner {...props} />;
  const next = encodeURIComponent('/honorarrechner');
  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-sm p-5" data-testid="anfrage-login-hinweis">
      <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-1">{t('login_pflicht.honorar_kicker')}</div>
      <p className="text-sm text-[#34536b]">{t('login_pflicht.honorar_text')}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <a href={`/login?next=${next}`} className="px-5 py-2.5 bg-[#1B3A5C] text-white text-xs tracking-wider rounded-sm" data-testid="anfrage-login-anmelden">{t('login_pflicht.anmelden')}</a>
        <a href={`/register?next=${next}`} className="px-5 py-2.5 border border-[#b8893d] text-[#1B3A5C] text-xs tracking-wider rounded-sm" data-testid="anfrage-login-registrieren">{t('login_pflicht.registrieren')}</a>
      </div>
    </div>
  );
};

const MobileBar = ({ targetId, big, label, busy }) => {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const el = document.getElementById(targetId);
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setHidden(e.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [targetId]);
  if (hidden) return null;
  return (
    <button type="button" onClick={() => document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })} data-testid="mobile-result-bar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#1B3A5C] text-white px-5 py-3 flex items-center justify-between shadow-[0_-6px_20px_rgba(0,0,0,0.15)] border-t border-[#b8893d]/40">
      <div className="flex items-baseline gap-2"><span className="font-serif text-2xl leading-none" data-testid="mobile-result-big">{busy ? '…' : big}</span><span className="text-xs text-white/70">{label}</span></div>
      <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] text-[#e0b56a]">{t('honorar.mobile_bar.zum_ergebnis')} <ChevronDown className="w-4 h-4" /></span>
    </button>
  );
};

/* ---------------- StBVV ---------------- */
const ARTEN_KEYS = [
  { key: 'einzel', icon: Store, titleKey: 'honorar.stbvv.arten.einzel.title', descKey: 'honorar.stbvv.arten.einzel.desc' },
  { key: 'freiberufler', icon: Stethoscope, titleKey: 'honorar.stbvv.arten.freiberufler.title', descKey: 'honorar.stbvv.arten.freiberufler.desc' },
  { key: 'personen', icon: Users, titleKey: 'honorar.stbvv.arten.personen.title', descKey: 'honorar.stbvv.arten.personen.desc' },
  { key: 'kapital', icon: Building2, titleKey: 'honorar.stbvv.arten.kapital.title', descKey: 'honorar.stbvv.arten.kapital.desc' },
];
const UMSATZ_CHIPS = [30000, 50000, 100000, 250000, 500000];
const LEISTUNGEN_KEYS = [
  { key: 'buchfuehrung', titleKey: 'honorar.stbvv.leistungen.buchfuehrung.title', descKey: 'honorar.stbvv.leistungen.buchfuehrung.desc' },
  { key: 'abschluss', titleKey: 'honorar.stbvv.leistungen.abschluss.title', descKey: 'honorar.stbvv.leistungen.abschluss.desc' },
  { key: 'erklaerungen', titleKey: 'honorar.stbvv.leistungen.erklaerungen.title', descKey: 'honorar.stbvv.leistungen.erklaerungen.desc' },
];

const StbvvRechner = () => {
  const { t } = useTranslation();
  const [f, setF] = useState({ rechtsform: 'einzel', umsatz: '50.000', gewinnManuell: false, gewinn: '', mitarbeiter: 0, buchfuehrung: true, abschluss: true, erklaerungen: true });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const umsatz = num(f.umsatz);
  const body = useMemo(() => ({
    rechtsform: f.rechtsform, umsatz: umsatz || 0, mitarbeiter: f.mitarbeiter,
    buchfuehrung: f.buchfuehrung, abschluss: f.abschluss, erklaerungen: f.erklaerungen,
    ...(f.gewinnManuell && num(f.gewinn) != null ? { gewinn: num(f.gewinn) } : {}),
  }), [f, umsatz]);
  const { erg, busy } = useDebouncedCalc('/honorar/stbvv', body, umsatz != null && umsatz >= 0);
  const mittelMonat = erg?.monat?.mittel?.brutto;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="stbvv-rechner">
      <div className="lg:col-span-3 space-y-8">
        <div>
          <div className={label}>{t('honorar.stbvv.frage_rechtsform')}</div>
          <div className="grid grid-cols-2 gap-3" data-testid="stbvv-rechtsform">
            {ARTEN_KEYS.map((a) => (
              <button key={a.key} type="button" onClick={() => set('rechtsform', a.key)} data-testid={`stbvv-art-${a.key}`} className={`text-left p-4 border-2 rounded-sm transition-colors ${f.rechtsform === a.key ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200 hover:border-[#b8893d]/50'}`}>
                <a.icon className={`w-5 h-5 mb-2 ${f.rechtsform === a.key ? 'text-[#b8893d]' : 'text-[#1B3A5C]'}`} />
                <div className="text-sm font-medium text-[#1B3A5C]">{t(a.titleKey)}</div>
                <div className="text-[11px] text-[#7a8a99] leading-snug mt-0.5">{t(a.descKey)}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className={label}>{t('honorar.stbvv.frage_umsatz')}</div>
          <MoneyInput value={f.umsatz} onChange={(v) => set('umsatz', v)} testid="stbvv-umsatz" placeholder={t('honorar.stbvv.umsatz_placeholder')} />
          <Chips values={UMSATZ_CHIPS} current={umsatz} onPick={(v) => set('umsatz', fmtIn(v))} testid="stbvv-umsatz-chips" />
        </div>
        <div>
          <div className={label}>{t('honorar.stbvv.frage_gewinn')}</div>
          {!f.gewinnManuell ? (
            <div className="flex flex-wrap items-center gap-3 p-4 bg-[#f5f8fa] rounded-sm" data-testid="stbvv-gewinn-auto">
              <div className="text-sm text-[#34536b]">{t('honorar.stbvv.gewinn_auto_text_vor')} <strong className="text-[#1B3A5C]">{t('honorar.stbvv.gewinn_auto_prozent')}</strong>{umsatz ? <> ≈ <strong className="text-[#1B3A5C]">{eur0(umsatz * 0.25)}</strong></> : null}.</div>
              <button type="button" onClick={() => set('gewinnManuell', true)} className="inline-flex items-center gap-1 text-xs text-[#b8893d] underline" data-testid="stbvv-gewinn-edit"><Pencil className="w-3 h-3" /> {t('honorar.stbvv.gewinn_selbst_eingeben')}</button>
            </div>
          ) : (
            <div className="flex gap-3 items-start">
              <div className="flex-1"><MoneyInput value={f.gewinn} onChange={(v) => set('gewinn', v)} testid="stbvv-gewinn" placeholder={t('honorar.stbvv.gewinn_placeholder')} /></div>
              <button type="button" onClick={() => set('gewinnManuell', false)} className="text-xs text-[#7a8a99] underline pt-4 whitespace-nowrap" data-testid="stbvv-gewinn-auto-btn">{t('honorar.stbvv.gewinn_pauschal')}</button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className={label.replace('mb-2', 'mb-0')}>{t('honorar.stbvv.frage_mitarbeiter')}</div>
          <Stepper value={f.mitarbeiter} onChange={(v) => set('mitarbeiter', v)} testid="stbvv-mitarbeiter" />
        </div>
        {erg?.lohn && (
          <p className="text-[11px] text-[#7a8a99] -mt-4" data-testid="stbvv-lohn-extra">
            {t('honorar.stbvv.lohn_extra', { mitarbeiter: erg.lohn.mitarbeiter, pauschale: eur0(erg.lohn.pauschale), monat: eur0(erg.lohn.monat.netto) })}
          </p>
        )}
        <div>
          <div className={label}>{t('honorar.stbvv.frage_leistungen')}</div>
          <div className="space-y-2" data-testid="stbvv-leistungen">
            {LEISTUNGEN_KEYS.map((l) => (
              <label key={l.key} className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer ${f[l.key] ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200'}`}>
                <input type="checkbox" className="w-4 h-4 accent-[#b8893d]" checked={f[l.key]} onChange={(e) => set(l.key, e.target.checked)} data-testid={`stbvv-${l.key}`} />
                <div><div className="text-sm text-[#1B3A5C] font-medium">{t(l.titleKey)}</div><div className="text-[11px] text-[#7a8a99]">{t(l.descKey)}</div></div>
                {erg && <div className="ml-auto text-sm text-[#1B3A5C] whitespace-nowrap">{f[l.key] ? eur0(erg.positionen.filter((p) => ({ buchfuehrung: ['buchfuehrung'], abschluss: ['jahresabschluss', 'anhang', 'euer'], erklaerungen: ['ust', 'gewst', 'kst', 'est', 'feststellung'] })[l.key].includes(p.key)).reduce((a, p) => a + p.mittel, 0) / 12) + ' ' + t('honorar.stbvv.netto_pro_monat') : '—'}</div>}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:col-span-2 lg:sticky lg:top-40 self-start">
        <ResultCard kicker={t('honorar.stbvv.result_kicker')} big={erg ? eur0(erg.monat.mittel.netto) : '—'} bigLabel={t('honorar.stbvv.result_label')} busy={busy && !erg} testid="stbvv-ergebnis"
          range={erg ? t('honorar.stbvv.result_range', { min: eur0(erg.monat.min.netto), jahr: eur0(erg.jahr.mittel.netto) }) : null}
          sub={erg ? t('honorar.stbvv.result_sub', { monat_brutto: eur0(mittelMonat), jahr_brutto: eur0(erg.jahr.mittel.brutto), bilanzierung: erg.eingaben.bilanziert ? t('honorar.stbvv.bilanzierung') : t('honorar.stbvv.euer'), gewinn: eur0(erg.eingaben.gewinn), lohn_text: erg.lohn ? t('honorar.stbvv.result_lohn', { lohn: eur0(erg.lohn.monat.netto) }) : '' }) : null}>
          {erg && (
            <Details testid="stbvv-details">
              <div className="space-y-2 text-sm">
                {erg.positionen.map((p) => (
                  <div key={p.key} className="flex justify-between gap-3 border-b border-white/10 pb-2">
                    <div><div className="text-white/90">{p.text.split(' (')[0]}</div><div className="text-[10px] text-white/50">{p.rahmen}{p.gegenstandswert ? t('honorar.stbvv.details_wert', { wert: eur0(p.gegenstandswert) }) : ''}</div></div>
                    <div className="text-right whitespace-nowrap"><div className="text-white">{eur0(p.mittel)} {t('honorar.stbvv.details_jahr')}</div><div className="text-[10px] text-white/50">{t('honorar.stbvv.details_ab', { min: eur0(p.min) })}</div></div>
                  </div>
                ))}
                <div className="flex justify-between pt-1 text-white/80"><span>{t('honorar.stbvv.details_netto_jahr')}</span><span>{eur(erg.jahr.mittel.netto)}</span></div>
                <div className="flex justify-between text-white/80"><span>{t('honorar.stbvv.details_ust')}</span><span>{eur(erg.jahr.mittel.ust)}</span></div>
                <div className="flex justify-between font-medium"><span>{t('honorar.stbvv.details_brutto_jahr')}</span><span data-testid="stbvv-brutto-jahr">{eur(erg.jahr.mittel.brutto)}</span></div>
                {erg.lohn && (
                  <div className="flex justify-between gap-3 pt-2 text-[11px] text-white/60" data-testid="stbvv-lohn-details">
                    <span>{t('honorar.stbvv.details_lohn', { mitarbeiter: erg.lohn.mitarbeiter, pauschale: eur0(erg.lohn.pauschale) })}</span>
                    <span className="whitespace-nowrap">{eur(erg.lohn.monat.netto)} {t('honorar.stbvv.details_lohn_monat')}</span>
                  </div>
                )}
              </div>
              <div className="mt-4"><Hinweise items={[...erg.annahmen, ...erg.hinweise, erg.stand]} testid="stbvv-hinweise" /></div>
            </Details>
          )}
        </ResultCard>
        {erg && <AnfrageForm typ="stbvv" eingaben={erg.eingaben} ergebnis={erg} />}
      </div>
      <MobileBar targetId="stbvv-ergebnis" big={erg ? eur0(erg.monat.mittel.netto) : '—'} label={t('honorar.stbvv.mobile_label')} busy={busy && !erg} />
    </div>
  );
};

/* ---------------- RVG ---------------- */
const RVG_FAELLE_KEYS = [
  { key: 'erstberatung', labelKey: 'honorar.rvg.faelle.erstberatung.label', wert: null, hintKey: 'honorar.rvg.faelle.erstberatung.hint' },
  { key: 'aufenthalt', labelKey: 'honorar.rvg.faelle.aufenthalt.label', wert: 5000, hintKey: 'honorar.rvg.faelle.aufenthalt.hint', gruppe: 'zivil' },
  { key: 'einbuergerung', labelKey: 'honorar.rvg.faelle.einbuergerung.label', wert: 10000, hintKey: 'honorar.rvg.faelle.einbuergerung.hint', gruppe: 'zivil' },
  { key: 'miete', labelKey: 'honorar.rvg.faelle.miete.label', wert: 9600, hintKey: 'honorar.rvg.faelle.miete.hint', gruppe: 'zivil' },
  { key: 'forderung', labelKey: 'honorar.rvg.faelle.forderung.label', wert: 25000, hintKey: 'honorar.rvg.faelle.forderung.hint', gruppe: 'zivil' },
  { key: 'eigen', labelKey: 'honorar.rvg.faelle.eigen.label', wert: null, hintKey: 'honorar.rvg.faelle.eigen.hint', gruppe: 'zivil' },
  { key: 'strafrecht', labelKey: 'honorar.rvg.faelle.strafrecht.label', wert: null, hintKey: 'honorar.rvg.faelle.strafrecht.hint', gruppe: 'straf' },
  { key: 'bussgeld', labelKey: 'honorar.rvg.faelle.bussgeld.label', wert: null, hintKey: 'honorar.rvg.faelle.bussgeld.hint', gruppe: 'straf' },
];
const STRAF_STADIEN_KEYS = [
  { key: 'ermittlung', titleKey: 'honorar.straf.stadien.ermittlung.title', descKey: 'honorar.straf.stadien.ermittlung.desc', stufe: '1' },
  { key: 'ag', titleKey: 'honorar.straf.stadien.ag.title', descKey: 'honorar.straf.stadien.ag.desc', stufe: '2' },
  { key: 'lg', titleKey: 'honorar.straf.stadien.lg.title', descKey: 'honorar.straf.stadien.lg.desc', stufe: '2' },
  { key: 'berufung', titleKey: 'honorar.straf.stadien.berufung.title', descKey: 'honorar.straf.stadien.berufung.desc', stufe: '3' },
  { key: 'revision', titleKey: 'honorar.straf.stadien.revision.title', descKey: 'honorar.straf.stadien.revision.desc', stufe: '3' },
];
const BUSSGELD_STADIEN_KEYS = [
  { key: 'behoerde', titleKey: 'honorar.bussgeld.stadien.behoerde.title', descKey: 'honorar.bussgeld.stadien.behoerde.desc', stufe: '1' },
  { key: 'gericht', titleKey: 'honorar.bussgeld.stadien.gericht.title', descKey: 'honorar.bussgeld.stadien.gericht.desc', stufe: '2' },
];
const BUSSGELD_CHIPS = [50, 200, 1000, 10000];

const StrafRechner = ({ bereich }) => {
  const { t } = useTranslation();
  const straf = bereich === 'strafrecht';
  const stadien = straf ? STRAF_STADIEN_KEYS : BUSSGELD_STADIEN_KEYS;
  const [stadium, setStadium] = useState(straf ? 'ermittlung' : 'behoerde');
  const [tage, setTage] = useState(1);
  const [busse, setBusse] = useState('50');
  const [unternehmer, setUnternehmer] = useState(false);
  const geldbusse = num(busse) ?? 0;
  const mitTermin = straf ? stadium !== 'ermittlung' : stadium === 'gericht';
  const body = useMemo(() => ({ bereich, stadium, verhandlungstage: mitTermin ? tage : 0, geldbusse }), [bereich, stadium, mitTermin, tage, geldbusse]);
  const { erg, busy } = useDebouncedCalc('/honorar/straf', body, true);
  const g = erg?.gesamt;
  const k = unternehmer ? 'netto' : 'brutto';
  let strafSub = null;
  if (g && unternehmer) strafSub = t('honorar.straf.result_sub_brutto', { brutto: eur(g.mittel.brutto) });
  else if (g) strafSub = t('honorar.straf.result_sub_netto', { netto: eur(g.mittel.netto), ust: eur(g.mittel.ust) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="straf-rechner">
      <div className="lg:col-span-3 space-y-8">
        {!straf && (
          <div>
            <div className={label}>{t('honorar.bussgeld.frage_geldbusse')}</div>
            <MoneyInput value={busse} onChange={setBusse} testid="straf-geldbusse" placeholder={t('honorar.bussgeld.geldbusse_placeholder')} />
            <Chips values={BUSSGELD_CHIPS} current={geldbusse} onPick={(v) => setBusse(fmtIn(v))} testid="straf-geldbusse-chips" />
            <p className="text-[11px] text-[#7a8a99] mt-2">{t('honorar.bussgeld.geldbusse_hinweis')}</p>
          </div>
        )}
        <div>
          <div className={label}>{t(straf ? 'honorar.straf.frage_stadien' : 'honorar.bussgeld.frage_stadien')}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="straf-stadien">
            {stadien.map((u) => (
              <button key={u.key} type="button" onClick={() => setStadium(u.key)} data-testid={`straf-stadium-${u.key}`} className={`text-left p-3 border-2 rounded-sm transition-colors flex gap-3 ${stadium === u.key ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200 hover:border-[#b8893d]/50'}`}>
                <span className={`w-6 h-6 rounded-full text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5 ${stadium === u.key ? 'bg-[#b8893d] text-white' : 'bg-[#f5f8fa] text-[#1B3A5C]'}`}>{u.stufe}</span>
                <div><div className="text-sm font-medium text-[#1B3A5C]">{t(u.titleKey)}</div><div className="text-[11px] text-[#7a8a99] leading-snug">{t(u.descKey)}</div></div>
              </button>
            ))}
          </div>
        </div>
        {mitTermin && (
          <div>
            <div className={label}>{t(straf ? 'honorar.straf.frage_tage' : 'honorar.bussgeld.frage_tage')}</div>
            <div className="flex items-center gap-4">
              <Stepper value={tage} onChange={setTage} testid="straf-tage" />
              <p className="text-[11px] text-[#7a8a99]">{t(straf ? 'honorar.straf.tage_hinweis' : 'honorar.bussgeld.tage_hinweis')}</p>
            </div>
          </div>
        )}
        <div className="p-4 bg-[#fbf7f0] border border-[#e5d8b8] rounded-sm text-xs text-[#34536b] leading-relaxed flex gap-2" data-testid="straf-erklaerung">
          <Info className="w-4 h-4 text-[#b8893d] flex-shrink-0 mt-0.5" />
          <span>{t(straf ? 'honorar.straf.erklaerung' : 'honorar.bussgeld.erklaerung')}</span>
        </div>
      </div>
      <div className="lg:col-span-2 lg:sticky lg:top-40 self-start">
        <ResultCard kicker={t(straf ? 'honorar.straf.result_kicker' : 'honorar.bussgeld.result_kicker')} big={g ? eur0(g.mittel[k]) : '—'} bigLabel={t('honorar.straf.result_label', { typ: k })} busy={busy && !erg} testid="straf-ergebnis" toggle={{ checked: unternehmer, onChange: setUnternehmer }}
          range={g ? t('honorar.straf.result_range', { min: eur(g.min[k]), max: eur(g.max[k]), typ: k }) : null}
          sub={strafSub}>
          {erg && (
            <Details testid="straf-details">
              <div className="space-y-2 text-sm">
                {erg.positionen.map((p, i) => <div key={`${p.nr}-${i}`} className="flex justify-between gap-3 border-b border-white/10 pb-2"><div><div className="text-white/90">{p.text}</div><div className="text-[10px] text-white/50">{p.nr} · {t('honorar.straf.details_rahmen', { min: eur(p.min), max: eur(p.max) })}</div></div><div className="whitespace-nowrap text-white">{eur(p.mittel)}</div></div>)}
                <div className="flex justify-between font-medium pt-1"><span>{t('honorar.straf.details_mittel_brutto')}</span><span data-testid="straf-brutto">{eur(g.mittel.brutto)}</span></div>
              </div>
              <div className="mt-4"><Hinweise items={[...erg.hinweise, erg.stand]} testid="straf-hinweise" /></div>
            </Details>
          )}
        </ResultCard>
        {erg && <AnfrageForm typ="straf" eingaben={body} ergebnis={erg} />}
      </div>
      <MobileBar targetId="straf-ergebnis" big={g ? eur0(g.mittel[k]) : '—'} label={t('honorar.straf.mobile_label', { typ: k })} busy={busy && !erg} />
    </div>
  );
};
const UMFANG_KEYS = [
  { key: 'aussergerichtlich', titleKey: 'honorar.rvg.umfang.aussergerichtlich.title', descKey: 'honorar.rvg.umfang.aussergerichtlich.desc', stufe: '1' },
  { key: 'aussergerichtlich_einigung', titleKey: 'honorar.rvg.umfang.aussergerichtlich_einigung.title', descKey: 'honorar.rvg.umfang.aussergerichtlich_einigung.desc', stufe: '1' },
  { key: 'klage', titleKey: 'honorar.rvg.umfang.klage.title', descKey: 'honorar.rvg.umfang.klage.desc', stufe: '2' },
  { key: 'klage_einigung', titleKey: 'honorar.rvg.umfang.klage_einigung.title', descKey: 'honorar.rvg.umfang.klage_einigung.desc', stufe: '2' },
  { key: 'berufung', titleKey: 'honorar.rvg.umfang.berufung.title', descKey: 'honorar.rvg.umfang.berufung.desc', stufe: '3' },
  { key: 'berufung_einigung', titleKey: 'honorar.rvg.umfang.berufung_einigung.title', descKey: 'honorar.rvg.umfang.berufung_einigung.desc', stufe: '3' },
];
const INSTANZEN_KEYS = [
  { stufe: '1', titelKey: 'honorar.rvg.instanzen.stufe1.titel', textKey: 'honorar.rvg.instanzen.stufe1.text' },
  { stufe: '2', titelKey: 'honorar.rvg.instanzen.stufe2.titel', textKey: 'honorar.rvg.instanzen.stufe2.text' },
  { stufe: '3', titelKey: 'honorar.rvg.instanzen.stufe3.titel', textKey: 'honorar.rvg.instanzen.stufe3.text' },
  { stufe: '✓', titelKey: 'honorar.rvg.instanzen.einigung.titel', textKey: 'honorar.rvg.instanzen.einigung.text' },
];

const InstanzenErklaerung = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-sm" data-testid="instanzen-erklaerung">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left" data-testid="instanzen-toggle">
        <span className="inline-flex items-center gap-2 text-sm text-[#1B3A5C]"><Info className="w-4 h-4 text-[#b8893d]" /> {t('honorar.rvg.instanzen.frage')}</span>
        <ChevronDown className={`w-4 h-4 text-[#7a8a99] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ol className="px-4 pb-4 space-y-3" data-testid="instanzen-liste">
          {INSTANZEN_KEYS.map((i) => (
            <li key={i.stufe} className="flex gap-3">
              <span className="w-7 h-7 rounded-full bg-[#1B3A5C] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i.stufe}</span>
              <div><div className="text-sm font-medium text-[#1B3A5C]">{t(i.titelKey)}</div><div className="text-xs text-[#34536b] leading-relaxed">{t(i.textKey)}</div></div>
            </li>
          ))}
          <li className="text-[11px] text-[#7a8a99] pl-10">{t('honorar.rvg.instanzen.hinweis')}</li>
        </ol>
      )}
    </div>
  );
};

const RvgRechner = () => {
  const { t } = useTranslation();
  const [fall, setFall] = useState('erstberatung');
  const [wert, setWert] = useState('5.000');
  const [umfang, setUmfang] = useState('aussergerichtlich');
  const [unternehmer, setUnternehmer] = useState(false);
  const [vorverfahren, setVorverfahren] = useState(false);
  const gw = num(wert);
  const istKlage = umfang.startsWith('klage');
  const istErst = fall === 'erstberatung';
  const { erg, busy } = useDebouncedCalc(istErst ? '/honorar/erstberatung' : '/honorar/rvg', istErst ? {} : { gegenstandswert: gw || 0, umfang, vorverfahren: istKlage && vorverfahren }, istErst || (!!gw && gw > 0));
  const pickFall = (k) => { setFall(k); const f = RVG_FAELLE_KEYS.find((x) => x.key === k); if (f.wert) setWert(fmtIn(f.wert)); };
  const istStraf = fall === 'strafrecht' || fall === 'bussgeld';
  const Tile = ({ c, big }) => (
    <button type="button" onClick={() => pickFall(c.key)} data-testid={`rvg-fall-${c.key}`} className={`text-left border-2 rounded-sm transition-colors ${big ? 'p-4 flex items-center gap-4 w-full' : 'p-3'} ${fall === c.key ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200 hover:border-[#b8893d]/50 bg-white'}`}>
      {big && <span className="w-10 h-10 rounded-full bg-[#b8893d] text-white flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5" /></span>}
      <div>
        {big && <div className="text-[10px] tracking-[0.25em] text-[#b8893d] uppercase mb-0.5">{t('honorar.rvg.empfehlung')}</div>}
        <div className={`font-medium text-[#1B3A5C] ${big ? 'text-base' : 'text-sm'}`}>{t(c.labelKey)}</div><div className="text-[11px] text-[#7a8a99]">{t(c.hintKey)}</div>
      </div>
    </button>
  );
  const faelle = (
    <div>
      <div className={label}>{t('honorar.rvg.frage_fall')}</div>
      <div className="space-y-5" data-testid="rvg-faelle">
        <Tile c={RVG_FAELLE_KEYS[0]} big />
        {[['zivil', 'honorar.rvg.gruppen.zivil.titel', 'honorar.rvg.gruppen.zivil.desc'], ['straf', 'honorar.rvg.gruppen.straf.titel', 'honorar.rvg.gruppen.straf.desc']].map(([g, titelKey, descKey]) => (
          <div key={g} data-testid={`rvg-gruppe-${g}`}>
            <div className="flex flex-wrap items-baseline gap-x-2 mb-2"><span className="text-xs font-medium text-[#1B3A5C]">{t(titelKey)}</span><span className="text-[11px] text-[#9aa5b1]">{t(descKey)}</span></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{RVG_FAELLE_KEYS.filter((c) => c.gruppe === g).map((c) => <Tile key={c.key} c={c} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
  const rvgBig = erg ? eur0(unternehmer ? erg.netto : erg.brutto) : '—';
  const rvgBigLabelKey = `honorar.rvg.result_label${istErst ? '_erst' : ''}_${unternehmer ? 'netto' : 'brutto'}`;
  let rvgRange = null;
  if (erg && unternehmer) rvgRange = t('honorar.rvg.result_range_brutto', { brutto: eur(erg.brutto) });
  else if (erg) rvgRange = t('honorar.rvg.result_range_netto', { netto: eur(erg.netto), ust: eur(erg.ust) });
  let rvgSub = null;
  if (erg && istErst) rvgSub = t('honorar.rvg.result_sub_erst');
  else if (erg) rvgSub = t('honorar.rvg.result_sub', { gegenstandswert: eur0(erg.gegenstandswert), gebuehr: eur(erg.gebuehr_1_0) });
  if (istStraf) {
    return (
      <div className="space-y-8" data-testid="rvg-rechner">
        <div className="lg:w-3/5 lg:pr-8">{faelle}</div>
        <StrafRechner key={fall} bereich={fall} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="rvg-rechner">
      <div className="lg:col-span-3 space-y-8">
        {faelle}
        {istErst ? (
          <div className="p-5 bg-[#fbf7f0] border border-[#e5d8b8] rounded-sm text-sm text-[#34536b] leading-relaxed space-y-2" data-testid="rvg-erstberatung-info">
            <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase">{t('honorar.rvg.erstberatung.ueberschrift')}</div>
            <p>{t('honorar.rvg.erstberatung.text_vor')} <strong className="text-[#1B3A5C]">{t('honorar.rvg.erstberatung.text_preis')}</strong>{t('honorar.rvg.erstberatung.text_nach')}</p>
            <p className="text-xs text-[#7a8a99]">{t('honorar.rvg.erstberatung.hinweis')}</p>
          </div>
        ) : (<>
        <div>
          <div className={label}>{t('honorar.rvg.frage_wert')}</div>
          <MoneyInput value={wert} onChange={(v) => { setWert(v); setFall('eigen'); }} testid="rvg-wert" placeholder={t('honorar.rvg.wert_placeholder')} />
          <p className="text-[11px] text-[#7a8a99] mt-2">{t('honorar.rvg.wert_hinweis')}</p>
        </div>
        <div>
          <div className={label}>{t('honorar.rvg.frage_umfang')}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rvg-umfang">
            {UMFANG_KEYS.map((u) => (
              <button key={u.key} type="button" onClick={() => setUmfang(u.key)} data-testid={`rvg-umfang-${u.key}`} className={`text-left p-3 border-2 rounded-sm transition-colors flex gap-3 ${umfang === u.key ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200 hover:border-[#b8893d]/50'}`}>
                <span className={`w-6 h-6 rounded-full text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5 ${umfang === u.key ? 'bg-[#b8893d] text-white' : 'bg-[#f5f8fa] text-[#1B3A5C]'}`}>{u.stufe}</span>
                <div><div className="text-sm font-medium text-[#1B3A5C]">{t(u.titleKey)}</div><div className="text-[11px] text-[#7a8a99] leading-snug">{t(u.descKey)}</div></div>
              </button>
            ))}
          </div>
          {istKlage && (
            <label className="mt-3 flex items-start gap-3 p-3 bg-[#fbf7f0] border border-[#e5d8b8] rounded-sm text-sm text-[#34536b] cursor-pointer" data-testid="rvg-vorverfahren-box">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d]" checked={vorverfahren} onChange={(e) => setVorverfahren(e.target.checked)} data-testid="rvg-vorverfahren" />
              <span><strong className="text-[#1B3A5C]">{t('honorar.rvg.vorverfahren.titel')}</strong> – {t('honorar.rvg.vorverfahren.text_vor')} <strong>{t('honorar.rvg.vorverfahren.text_anrechnung')}</strong>. {t('honorar.rvg.vorverfahren.text_nach')}</span>
            </label>
          )}
          <InstanzenErklaerung />
        </div>
        </>)}
      </div>
      <div className="lg:col-span-2 lg:sticky lg:top-40 self-start">
        <ResultCard kicker={t(istErst ? 'honorar.rvg.result_kicker_erst' : 'honorar.rvg.result_kicker')} big={rvgBig} bigLabel={t(rvgBigLabelKey)} busy={busy && !erg} testid="rvg-ergebnis" toggle={{ checked: unternehmer, onChange: setUnternehmer }}
          range={rvgRange} sub={rvgSub}>
          {erg && (
            <Details testid="rvg-details">
              <div className="space-y-2 text-sm">
                {erg.positionen.map((p, i) => <div key={`${p.nr}-${i}`} className="flex justify-between gap-3 border-b border-white/10 pb-2"><div><div className="text-white/90">{p.text}</div><div className="text-[10px] text-white/50">{p.nr}</div></div><div className={`whitespace-nowrap ${p.betrag < 0 ? 'text-[#e0b56a]' : 'text-white'}`}>{eur(p.betrag)}</div></div>)}
                <div className="flex justify-between font-medium pt-1"><span>{t('honorar.rvg.details_brutto')}</span><span data-testid="rvg-brutto">{eur(erg.brutto)}</span></div>
              </div>
              <div className="mt-4"><Hinweise items={[...erg.hinweise, erg.stand]} testid="rvg-hinweise" /></div>
            </Details>
          )}
        </ResultCard>
        {erg && <AnfrageForm typ="rvg" eingaben={istErst ? { fall } : { gegenstandswert: erg.gegenstandswert, umfang, fall, vorverfahren: istKlage && vorverfahren }} ergebnis={erg} />}
      </div>
      <MobileBar targetId="rvg-ergebnis" big={rvgBig} label={unternehmer ? t('honorar.rvg.mobile_netto') : t('honorar.rvg.mobile_brutto')} busy={busy && !erg} />
    </div>
  );
};

/* ---------------- Private Steuererklärung ---------------- */
const LOHN_CHIPS = [15000, 30000, 45000, 60000, 90000];

const Toggle = ({ checked, onChange, title, desc, children, testid }) => (
  <div className={`p-4 border rounded-sm ${checked ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200'}`}>
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" className="w-4 h-4 accent-[#b8893d]" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <div><div className="text-sm text-[#1B3A5C] font-medium">{title}</div><div className="text-[11px] text-[#7a8a99]">{desc}</div></div>
    </label>
    {checked && <div className="mt-3">{children}</div>}
  </div>
);

const EstRechner = () => {
  const { t } = useTranslation();
  const [f, setF] = useState({ arbeitslohn: '15.000', partner: false, arbeitslohn_partner: '', vermietung: false, mieteinnahmen: '', vermietung_objekte: 1, kapital: false, kapitalertraege: '' });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const body = useMemo(() => ({
    arbeitslohn: num(f.arbeitslohn) || 0, arbeitslohn_partner: f.partner ? num(f.arbeitslohn_partner) || 0 : 0,
    mieteinnahmen: f.vermietung ? num(f.mieteinnahmen) || 0 : 0, vermietung_objekte: f.vermietung ? f.vermietung_objekte : 0,
    kapitalertraege: f.kapital ? num(f.kapitalertraege) || 0 : 0,
  }), [f]);
  const { erg, busy } = useDebouncedCalc('/honorar/est', body, true);
  const tg = (k) => ({ checked: f[k], onChange: (v) => set(k, v) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="est-rechner">
      <div className="lg:col-span-3 space-y-8">
        <div>
          <div className={label}>{t('honorar.est.frage_arbeitslohn')}</div>
          <MoneyInput value={f.arbeitslohn} onChange={(v) => set('arbeitslohn', v)} testid="est-arbeitslohn" placeholder={t('honorar.est.arbeitslohn_placeholder')} />
          <Chips values={LOHN_CHIPS} current={num(f.arbeitslohn)} onPick={(v) => set('arbeitslohn', fmtIn(v))} testid="est-lohn-chips" />
        </div>
        <div>
          <div className={label}>{t('honorar.est.frage_zusatz')}</div>
          <div className="space-y-2">
            <Toggle {...tg('partner')} title={t('honorar.est.partner.title')} desc={t('honorar.est.partner.desc')} testid="est-partner">
              <MoneyInput value={f.arbeitslohn_partner} onChange={(v) => set('arbeitslohn_partner', v)} testid="est-arbeitslohn-partner" placeholder={t('honorar.est.partner.placeholder')} />
            </Toggle>
            <Toggle {...tg('vermietung')} title={t('honorar.est.vermietung.title')} desc={t('honorar.est.vermietung.desc')} testid="est-vermietung">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[180px]"><MoneyInput value={f.mieteinnahmen} onChange={(v) => set('mieteinnahmen', v)} testid="est-mieteinnahmen" placeholder={t('honorar.est.vermietung.placeholder')} /></div>
                <div className="flex items-center gap-2 text-xs text-[#34536b]">{t('honorar.est.vermietung.objekte')} <Stepper value={f.vermietung_objekte} onChange={(v) => set('vermietung_objekte', Math.max(1, v))} testid="est-objekte" /></div>
              </div>
            </Toggle>
            <Toggle {...tg('kapital')} title={t('honorar.est.kapital.title')} desc={t('honorar.est.kapital.desc')} testid="est-kapital">
              <MoneyInput value={f.kapitalertraege} onChange={(v) => set('kapitalertraege', v)} testid="est-kapitalertraege" placeholder={t('honorar.est.kapital.placeholder')} />
            </Toggle>
          </div>
        </div>
      </div>
      <div className="lg:col-span-2 lg:sticky lg:top-40 self-start">
        <ResultCard kicker={t('honorar.est.result_kicker')} big={erg ? eur0(erg.gesamt.mittel.brutto) : '—'} bigLabel={t('honorar.est.result_label')} busy={busy && !erg} testid="est-ergebnis"
          range={erg ? t('honorar.est.result_range', { min: eur0(erg.gesamt.min.brutto) }) : null}
          sub={erg ? t('honorar.est.result_sub', { einkuenfte: eur0(erg.einkuenfte_summe) }) : null}>
          {erg && (
            <Details testid="est-details">
              <div className="space-y-2 text-sm">
                {erg.positionen.map((p) => (
                  <div key={p.key} className="flex justify-between gap-3 border-b border-white/10 pb-2">
                    <div><div className="text-white/90">{p.text.split(' (§')[0]}</div><div className="text-[10px] text-white/50">{p.rahmen} · {t('honorar.est.details_wert', { wert: eur0(p.gegenstandswert) })}</div></div>
                    <div className="text-right whitespace-nowrap"><div className="text-white">{eur0(p.mittel)}</div><div className="text-[10px] text-white/50">{t('honorar.est.details_ab', { min: eur0(p.min) })}</div></div>
                  </div>
                ))}
                <div className="flex justify-between font-medium pt-1"><span>{t('honorar.est.details_brutto')}</span><span data-testid="est-brutto">{eur(erg.gesamt.mittel.brutto)}</span></div>
              </div>
              <div className="mt-4"><Hinweise items={[...(erg.annahmen || []), ...erg.hinweise, erg.stand]} testid="est-hinweise" /></div>
            </Details>
          )}
        </ResultCard>
        {erg && <AnfrageForm typ="est" eingaben={erg.eingaben} ergebnis={erg} />}
      </div>
      <MobileBar targetId="est-ergebnis" big={erg ? eur0(erg.gesamt.mittel.brutto) : '—'} label={t('honorar.est.mobile_label')} busy={busy && !erg} />
    </div>
  );
};

const TABS_KEYS = [
  { key: 'stbvv', icon: Calculator, titleKey: 'honorar.tabs.stbvv.title', descKey: 'honorar.tabs.stbvv.desc' },
  { key: 'est', icon: FileText, titleKey: 'honorar.tabs.est.title', descKey: 'honorar.tabs.est.desc' },
  { key: 'rvg', icon: Scale, titleKey: 'honorar.tabs.rvg.title', descKey: 'honorar.tabs.rvg.desc' },
];

const Honorarrechner = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('stbvv');
  return (
    <div className="min-h-screen bg-white">
      <Seo title={t('honorar.seo.title')} description={t('honorar.seo.description')} path="/honorarrechner" />
      <Header />
      <main className="pt-[150px] pb-16 lg:pb-0">
        <section className="bg-[#1B3A5C] text-white py-14 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"><div className="absolute top-0 right-0 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div></div>
          <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4"><span className="w-12 h-px bg-[#b8893d]"></span>{t('honorar.hero.kicker')}</div>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-3">{t('honorar.hero.title')}</h1>
            <p className="text-white/80 leading-relaxed max-w-3xl">{t('honorar.hero.subtitle')}</p>
          </div>
        </section>
        <section className="py-12 bg-[#fafbfc]">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10" data-testid="honorar-tabs">
              {TABS_KEYS.map(({ key: k, icon: Icon, titleKey, descKey }) => (
                <button key={k} type="button" onClick={() => setTab(k)} data-testid={`tab-${k}`} aria-pressed={tab === k} className={`text-left p-4 rounded-sm border-2 transition-colors flex gap-3 items-start ${tab === k ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200 hover:border-[#b8893d]'}`}>
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${tab === k ? 'bg-[#b8893d] text-white' : 'bg-[#f5f8fa] text-[#b8893d]'}`}><Icon className="w-4 h-4" /></span>
                  <span><span className="block text-xs tracking-[0.15em] font-medium">{t(titleKey).toUpperCase()}</span><span className={`block text-[11px] leading-snug mt-1 ${tab === k ? 'text-white/70' : 'text-[#7a8a99]'}`}>{t(descKey)}</span></span>
                </button>
              ))}
            </div>
            {tab === 'stbvv' ? <StbvvRechner /> : tab === 'est' ? <EstRechner /> : <RvgRechner />}
            <p className="mt-12 text-xs text-[#9aa5b1] leading-relaxed max-w-4xl" data-testid="honorar-disclaimer"><strong className="text-[#1B3A5C]">{t('honorar.disclaimer.ohne_gewaehr')}</strong> {t('honorar.disclaimer.text_1')} <strong className="text-[#1B3A5C]">{t('honorar.disclaimer.vorschuesse')}</strong> {t('honorar.disclaimer.text_2')} <strong className="text-[#1B3A5C]">{t('honorar.disclaimer.einzelauftraege')}</strong> {t('honorar.disclaimer.text_3')}</p>
          </div>
        </section>
      </main>
      <Footer />
      <div className="[&>button]:bottom-[72px] lg:[&>button]:bottom-6 [&>div]:bottom-[140px] lg:[&>div]:bottom-24"><ChatWidget /></div>
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Honorarrechner;

